import axios from 'axios';

/**
 * Refresh and sign-out calls for lsgo-authentication access tokens. Ported
 * from lsgo-teacher's util of the same name.
 *
 * lsgo-student-web is a public OAuth client, so the refresh token is never
 * handed to JS: lsgo-authentication keeps it in an HttpOnly, SameSite=Strict
 * cookie (`lsgo.refresh`) scoped to /token. Every request to /token, including
 * the code exchange, must be sent withCredentials: true, or the browser never
 * stores or sends that cookie.
 */

/**
 * Exchanges the refresh-token cookie for a new access token. Rejects (e.g.
 * { error: 'invalid_grant' }) if the refresh token is missing, expired or
 * already rotated.
 */
export async function refreshLsgoAuthenticationToken({ authBaseUrl, clientId }) {
    const res = await axios.post(
        `${authBaseUrl}/token`,
        new URLSearchParams({ grant_type: 'refresh_token', client_id: clientId }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, withCredentials: true }
    );
    return res.data; // { access_token, token_type, expires_in }
}

/**
 * Schedules one refresh `leadSeconds` before the access token expires.
 * Returns a cancel function; call it whenever the token changes or the
 * session ends, so timers never stack up.
 */
export function scheduleLsgoAuthenticationRefresh({ expiresInSeconds, leadSeconds = 15, onDue }) {
    const delayMs = Math.max((expiresInSeconds - leadSeconds) * 1000, 1000);
    const timerId = setTimeout(onDue, delayMs);
    return () => clearTimeout(timerId);
}

/**
 * Signs out of the lsgo-authentication session: revokes the refresh-token
 * cookie (POST /token/revoke), which also blocklists every access token
 * issued from it. Always 200, even with no session.
 */
export async function signOutLsgoAuthentication({ authBaseUrl }) {
    await axios.post(`${authBaseUrl}/token/revoke`, null, { withCredentials: true });
}
